import { LitElement, html, css } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { dispatch } from '../../utils/event-helpers';
import { resolveFileIcon, resolveFileIconColor } from '../../utils/file-icon-map';
import '../../elements/asc-file-upload-item/asc-file-upload-item';
import type { FileStatus } from '../../elements/asc-file-upload-item/asc-file-upload-item';

type ValidationReason = 'type' | 'size' | 'max-files';

interface FileEntry {
  file: File;
  status: FileStatus;
  progress: number;
  errorMessage?: string;
}

/**
 * A drag-and-drop and click-to-browse file upload control with client-side validation,
 * queued multi-file support, and per-file progress tracking.
 *
 * The component manages drop zone interaction, file acceptance validation, and the rendered
 * file queue; it does **not** perform the HTTP upload. Listen to `asc-file-upload-start`,
 * drive the upload externally, and reflect status and progress back into the queue by calling
 * `updateFile()`.
 *
 * When `multiple` is `true`, accepted files are appended to a persistent queue list rendered
 * below the drop zone. When `multiple` is `false`, the queue holds at most one file; accepting
 * a new file replaces any existing entry.
 *
 * ```html
 * <asc-file-upload accept=".pdf, image/*" multiple max-file-size="10485760">
 *   <span slot="label">
 *     <strong>Click to upload</strong> or drag and drop
 *   </span>
 *   <span slot="hint">PDF or image files (max 10 MB)</span>
 * </asc-file-upload>
 * ```
 *
 * @element asc-file-upload
 *
 * @fires asc-file-upload-start      - Fired for each file accepted into the queue. Detail: `{ file: File }`
 * @fires asc-file-upload-retry      - Re-dispatched from child item. Detail: `{ file: File }`
 * @fires asc-file-upload-remove     - Re-dispatched from child item after removal. Detail: `{ file: File }`
 * @fires asc-file-validation-error  - Fired for each rejected file. Detail: `{ file: File; reason: 'type' | 'size' | 'max-files' }`
 *
 * @slot label - Instructional text inside the drop zone. Falls back to "Click to upload or drag and drop".
 * @slot hint  - Supplementary constraint text below the label (e.g. file type / size info). Hidden when empty.
 *
 * @csspart drop-zone         - The interactive drop zone surface.
 * @csspart drop-zone-icon    - The upload icon wrapper inside the drop zone.
 * @csspart drop-zone-preview - The file-type preview icon in the bottom-right during drag-over.
 * @csspart drop-zone-label   - `<span>` wrapping the `label` slot.
 * @csspart drop-zone-hint    - `<span>` wrapping the `hint` slot.
 * @csspart file-list         - `<ul>` — the ordered list of queued file items.
 *
 * @cssprop --asc-file-upload-dropzone-bg                  - Drop zone idle background. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-file-upload-dropzone-border-color         - Drop zone idle border color. Default: `var(--asc-border-secondary)`
 * @cssprop --asc-file-upload-dropzone-border-width         - Drop zone idle border width. Default: `var(--asc-border-width)`
 * @cssprop --asc-file-upload-dropzone-border-radius        - Drop zone corner radius. Default: `var(--asc-radius-sm)`
 * @cssprop --asc-file-upload-dropzone-padding-y            - Drop zone vertical padding. Default: `var(--asc-spacing-xl)`
 * @cssprop --asc-file-upload-dropzone-padding-x            - Drop zone horizontal padding. Default: `var(--asc-spacing-3xl)`
 * @cssprop --asc-file-upload-dropzone-gap                  - Content gap inside drop zone. Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-file-upload-dropzone-border-color-dragover - Border color during drag-over. Default: `var(--asc-border-brand)`
 * @cssprop --asc-file-upload-dropzone-bg-disabled          - Drop zone background when disabled. Default: `var(--asc-bg-disabled_subtle)`
 * @cssprop --asc-file-upload-dropzone-border-color-disabled - Drop zone border color when disabled. Default: `var(--asc-border-disabled_subtle)`
 * @cssprop --asc-file-upload-icon-container-size           - Size of the upload icon container. Default: `40px`
 * @cssprop --asc-file-upload-icon-container-bg             - Background of the upload icon container. Default: `var(--asc-bg-secondary)`
 * @cssprop --asc-file-upload-icon-size                     - Size of the upload icon. Default: `20px`
 * @cssprop --asc-file-upload-icon-color                    - Color of the upload icon. Default: `var(--asc-fg-primary)`
 * @cssprop --asc-file-upload-preview-icon-size             - Size of the drag-over preview icon. Default: `40px`
 * @cssprop --asc-file-upload-label-action-color            - "Click to upload" text color. Default: `var(--asc-text-brand-secondary)`
 * @cssprop --asc-file-upload-label-text-color              - "or drag and drop" text color. Default: `var(--asc-text-tertiary)`
 * @cssprop --asc-file-upload-label-font-size               - Label font size. Default: `var(--asc-font-size-text-sm)`
 * @cssprop --asc-file-upload-label-line-height             - Label line height. Default: `var(--asc-line-height-text-sm)`
 * @cssprop --asc-file-upload-hint-color                    - Hint text color. Default: `var(--asc-text-tertiary)`
 * @cssprop --asc-file-upload-hint-font-size                - Hint font size. Default: `var(--asc-font-size-text-xs)`
 * @cssprop --asc-file-upload-hint-line-height              - Hint line height. Default: `var(--asc-line-height-text-xs)`
 * @cssprop --asc-file-upload-list-gap                      - Gap between the drop zone and file list items. Default: `var(--asc-spacing-xl)`
 */
@customElement('asc-file-upload')
export class AscFileUpload extends LitElement {
  static override styles = css`
    :host {
      display: block;
    }

    .host-layout {
      display: flex;
      flex-direction: column;
      gap: var(--asc-file-upload-list-gap, var(--asc-spacing-xl, 16px));
    }

    /* ═══════════════════════════════════════════════════════════
       DROP ZONE
       ═══════════════════════════════════════════════════════════ */
    .drop-zone {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
      box-sizing: border-box;
      padding:
        var(--asc-file-upload-dropzone-padding-y, var(--asc-spacing-xl, 16px))
        var(--asc-file-upload-dropzone-padding-x, var(--asc-spacing-3xl, 24px));
      background: var(--asc-file-upload-dropzone-bg, var(--asc-bg-primary, #fff));
      border: var(--asc-file-upload-dropzone-border-width, var(--asc-border-width, 1px))
        var(--asc-file-upload-dropzone-border-style, solid)
        var(--asc-file-upload-dropzone-border-color, var(--asc-border-secondary, #a5b8bf));
      border-radius: var(--asc-file-upload-dropzone-border-radius, var(--asc-radius-sm, 8px));
      cursor: pointer;
      /* Remove button defaults */
      appearance: none;
      font-family: inherit;
      text-align: center;
      transition:
        background-color var(--asc-transition-fast),
        border-color var(--asc-transition-fast),
        border-width var(--asc-transition-fast);
    }

    .drop-zone:focus-visible {
      outline: var(--asc-focus-ring-width, 2px) solid var(--asc-focus-ring, #009e86);
      outline-offset: var(--asc-focus-ring-offset, 2px);
    }

    /* Drag-over state */
    .drop-zone--drag-over {
      border-color: var(
        --asc-file-upload-dropzone-border-color-dragover,
        var(--asc-border-brand, #008577)
      );
      border-width: 2px;
    }

    /* Disabled state */
    :host([disabled]) .drop-zone {
      background: var(
        --asc-file-upload-dropzone-bg-disabled,
        var(--asc-bg-disabled_subtle, #d3dadd)
      );
      border-color: var(
        --asc-file-upload-dropzone-border-color-disabled,
        var(--asc-border-disabled_subtle, #d3dadd)
      );
      cursor: not-allowed;
      /* Override native disabled button pointer-events:none so cursor feedback is visible */
      pointer-events: auto;
    }

    /* ═══════════════════════════════════════════════════════════
       DROP ZONE INNER CONTENT
       ═══════════════════════════════════════════════════════════ */
    .drop-zone-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--asc-file-upload-dropzone-gap, var(--asc-spacing-lg, 12px));
      width: 100%;
    }

    /* Upload icon container (featured icon style) */
    .icon-container {
      position: relative;
      width: var(--asc-file-upload-icon-container-size, 40px);
      height: var(--asc-file-upload-icon-container-size, 40px);
      border-radius: var(--asc-radius-sm, 8px);
      background: var(--asc-file-upload-icon-container-bg, var(--asc-bg-secondary, #e6eaec));
      border: 1px solid rgba(0, 0, 0, 0.12);
      box-shadow: 0px 1px 2px 0px var(--asc-shadow-xs, rgba(10, 13, 18, 0.05));
      overflow: clip;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .upload-icon {
      width: var(--asc-file-upload-icon-size, 20px);
      height: var(--asc-file-upload-icon-size, 20px);
      color: var(--asc-file-upload-icon-color, var(--asc-fg-primary, #30383b));
      display: block;
      flex-shrink: 0;
    }

    :host([disabled]) .upload-icon {
      color: var(--asc-fg-disabled, #8ea2a9);
    }

    /* Text area */
    .text-area {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--asc-spacing-xs, 4px);
      width: 100%;
    }

    .label-row {
      display: flex;
      gap: var(--asc-spacing-xs, 4px);
      align-items: flex-start;
      justify-content: center;
      width: 100%;
    }

    .label-action {
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-file-upload-label-font-size, var(--asc-font-size-text-sm, 14px));
      font-weight: var(--asc-font-weight-medium, 500);
      line-height: var(--asc-file-upload-label-line-height, var(--asc-line-height-text-sm, 20px));
      color: var(--asc-file-upload-label-action-color, var(--asc-text-brand-secondary, #004135));
      white-space: nowrap;
      margin: 0;
    }

    :host([disabled]) .label-action {
      color: var(--asc-fg-disabled, #8ea2a9);
    }

    .label-text {
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-file-upload-label-font-size, var(--asc-font-size-text-sm, 14px));
      font-weight: var(--asc-font-weight-regular, 400);
      line-height: var(--asc-file-upload-label-line-height, var(--asc-line-height-text-sm, 20px));
      color: var(--asc-file-upload-label-text-color, var(--asc-text-tertiary, #49565a));
      white-space: nowrap;
      margin: 0;
    }

    .label-slot-wrapper {
      display: flex;
      gap: var(--asc-spacing-xs, 4px);
      align-items: center;
      justify-content: center;
    }

    .hint-slot-wrapper {
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-file-upload-hint-font-size, var(--asc-font-size-text-xs, 12px));
      font-weight: var(--asc-font-weight-regular, 400);
      line-height: var(--asc-file-upload-hint-line-height, var(--asc-line-height-text-xs, 18px));
      color: var(--asc-file-upload-hint-color, var(--asc-text-tertiary, #49565a));
      text-align: center;
      width: 100%;
    }

    /* Drag-over file preview in bottom-right corner */
    .drag-preview {
      position: absolute;
      bottom: 16px;
      right: 16px;
      width: var(--asc-file-upload-preview-icon-size, 40px);
      height: var(--asc-file-upload-preview-icon-size, 40px);
      pointer-events: none;
    }

    .drag-preview__page svg {
      display: block;
      width: 100%;
      height: 100%;
    }

    .drag-preview__badge {
      position: absolute;
      inset: 45% 32.5% 15% 2.5%;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 2px;
      padding: 2px 3px;
      font-family: var(--asc-font-family-body);
      font-size: 8px;
      font-weight: var(--asc-font-weight-bold, 700);
      color: var(--asc-fg-white, #fff);
      line-height: 1;
      white-space: nowrap;
    }

    /* Visually hidden live region for drag feedback */
    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    /* ═══════════════════════════════════════════════════════════
       FILE LIST
       ═══════════════════════════════════════════════════════════ */
    .file-list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: var(--asc-file-upload-list-gap, var(--asc-spacing-xl, 16px));
      width: 100%;
    }

    .file-list[hidden] {
      display: none;
    }

    /* Hidden file input */
    input[type='file'] {
      display: none;
    }
  `;

  /** Comma-separated MIME types and/or file extensions accepted. Empty = all types. */
  @property({ type: String }) accept = '';

  /** Maximum allowed file size in bytes. 0 = no limit. */
  @property({ type: Number, attribute: 'max-file-size' }) maxFileSize = 0;

  /** When true, multiple files may be queued simultaneously. */
  @property({ type: Boolean, reflect: true }) multiple = false;

  /** Maximum number of queued files (when multiple=true). 0 = no limit. */
  @property({ type: Number, attribute: 'max-files' }) maxFiles = 0;

  /** When true, the drop zone is non-interactive and renders in a muted state. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  @state() private _files: FileEntry[] = [];
  @state() private _isDragOver = false;
  @state() private _dragPreviewType: string | null = null;
  @state() private _liveText = '';
  @state() private _hasHintSlotContent = false;
  // Counts dragenter calls minus dragleave calls so we only clear state when
  // the drag truly leaves the drop zone, not when crossing child element boundaries.
  private _dragCounter = 0;

  @query('input[type="file"]') private _input!: HTMLInputElement;

  /**
   * Updates the status and progress of a queued file by object identity.
   * Call this after receiving `asc-file-upload-start` or `asc-file-upload-retry`.
   */
  updateFile(
    file: File,
    update: {
      status: FileStatus;
      progress?: number;
      errorMessage?: string;
    }
  ): void {
    this._files = this._files.map((entry) =>
      entry.file === file
        ? {
            ...entry,
            status: update.status,
            progress: update.progress ?? entry.progress,
            errorMessage: Object.prototype.hasOwnProperty.call(update, 'errorMessage')
              ? update.errorMessage
              : entry.errorMessage,
          }
        : entry
    );
  }

  private _validate(files: FileList | null): void {
    if (!files || files.length === 0) return;

    let accepted = 0;

    for (const file of Array.from(files)) {
      // 1. Type check
      if (this.accept) {
        const tokens = this.accept.split(',').map((t) => t.trim());
        const matches = tokens.some((token) => {
          if (token.startsWith('.')) {
            return file.name.toLowerCase().endsWith(token.toLowerCase());
          }
          if (token.endsWith('/*')) {
            return file.type.startsWith(token.slice(0, -1));
          }
          return file.type === token;
        });
        if (!matches) {
          dispatch<{ file: File; reason: ValidationReason }>(
            this,
            'asc-file-validation-error',
            { detail: { file, reason: 'type' } }
          );
          continue;
        }
      }

      // 2. Size check
      if (this.maxFileSize > 0 && file.size > this.maxFileSize) {
        dispatch<{ file: File; reason: ValidationReason }>(
          this,
          'asc-file-validation-error',
          { detail: { file, reason: 'size' } }
        );
        continue;
      }

      // 3. Queue limit check
      if (this.multiple && this.maxFiles > 0) {
        if (this._files.length + accepted >= this.maxFiles) {
          dispatch<{ file: File; reason: ValidationReason }>(
            this,
            'asc-file-validation-error',
            { detail: { file, reason: 'max-files' } }
          );
          continue;
        }
      }

      // 4. Accept the file
      const entry: FileEntry = { file, status: 'uploading', progress: 0 };
      if (this.multiple) {
        this._files = [...this._files, entry];
      } else {
        this._files = [entry];
      }
      accepted++;
      dispatch<{ file: File }>(this, 'asc-file-upload-start', { detail: { file } });
      if (!this.multiple) break;
    }
  }

  private _handleZoneClick() {
    this._input.click();
  }

  private _handleHintSlotChange(event: Event) {
    const slot = event.target as HTMLSlotElement;
    this._hasHintSlotContent = slot
      .assignedNodes({ flatten: true })
      .some(
        (node) =>
          node.nodeType !== Node.TEXT_NODE || Boolean(node.textContent?.trim()),
      );
  }

  private _handleDragEnter(e: DragEvent) {
    if (this.disabled) return;
    e.preventDefault();
    this._dragCounter++;
    if (this._dragCounter !== 1) return; // already in drag-over state — skip re-resolving
    this._isDragOver = true;
    this._liveText = 'File detected — drop to upload';

    const item = e.dataTransfer?.items[0];
    if (item) {
      const mimeType = item.type;
      if (mimeType) {
        const rawSubtype = mimeType.split('/')[1] ?? '';
        const ext = rawSubtype.split('+')[0].split(';')[0];
        this._dragPreviewType = ext ? resolveFileIcon(`file.${ext}`) : null;
      } else {
        this._dragPreviewType = null;
      }
    }
  }

  private _handleDragOver(e: DragEvent) {
    if (this.disabled) return;
    e.preventDefault();
  }

  private _handleDragLeave(_e: DragEvent) {
    if (this.disabled) return;
    this._dragCounter = Math.max(0, this._dragCounter - 1);
    if (this._dragCounter > 0) return; // still inside a child — stay in drag-over state
    this._isDragOver = false;
    this._dragPreviewType = null;
    this._liveText = '';
  }

  private _handleDrop(e: DragEvent) {
    if (this.disabled) return;
    e.preventDefault();
    this._dragCounter = 0;
    this._isDragOver = false;
    this._dragPreviewType = null;
    this._liveText = '';
    this._validate(e.dataTransfer?.files ?? null);
  }

  private _handleInputChange(e: Event) {
    const input = e.target as HTMLInputElement;
    this._validate(input.files);
    // Reset so the same file can be re-selected
    input.value = '';
  }

  private _handleItemRetry(e: CustomEvent<{ file: File }>) {
    // Re-dispatch composed already set by child; relay for the implementer
    dispatch<{ file: File }>(this, 'asc-file-upload-retry', { detail: e.detail });
  }

  private _handleItemRemove(e: CustomEvent<{ file: File }>) {
    this._files = this._files.filter((entry) => entry.file !== e.detail.file);
    dispatch<{ file: File }>(this, 'asc-file-upload-remove', { detail: e.detail });
  }

  override render() {
    const showList = this.multiple && this._files.length > 0;

    return html`
      <div class="host-layout">
        <!-- Hidden live region for drag-over announcements -->
        <span class="sr-only" aria-live="polite" aria-atomic="true">${this._liveText}</span>

        <!-- Hidden file input -->
        <input
          type="file"
          aria-hidden="true"
          tabindex="-1"
          accept=${this.accept}
          ?multiple=${this.multiple}
          @change=${this._handleInputChange}
        />

        <!-- Drop zone -->
        <button
          type="button"
          part="drop-zone"
          class="drop-zone ${this._isDragOver ? 'drop-zone--drag-over' : ''}"
          aria-label="Upload files — click to browse or drag and drop"
          ?disabled=${this.disabled}
          @click=${this._handleZoneClick}
          @dragenter=${this._handleDragEnter}
          @dragover=${this._handleDragOver}
          @dragleave=${this._handleDragLeave}
          @drop=${this._handleDrop}
        >
          <div class="drop-zone-content">
            <!-- Upload icon container -->
            <div part="drop-zone-icon" class="icon-container">
              <svg
                class="upload-icon"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M2.5 14.165V15.832C2.5 16.752 3.247 17.499 4.167 17.499H15.833C16.753 17.499 17.5 16.752 17.5 15.832V14.165C17.5 13.705 17.127 13.332 16.667 13.332C16.207 13.332 15.833 13.705 15.833 14.165V15.832H4.167V14.165C4.167 13.705 3.793 13.332 3.333 13.332C2.873 13.332 2.5 13.705 2.5 14.165ZM6.908 7.325L9.167 5.066V12.499C9.167 12.959 9.54 13.332 10 13.332C10.46 13.332 10.833 12.959 10.833 12.499V5.066L13.092 7.325C13.417 7.65 13.942 7.65 14.267 7.325C14.592 7 14.592 6.475 14.267 6.15L10.592 2.475C10.267 2.15 9.742 2.15 9.417 2.475L5.733 6.15C5.408 6.475 5.408 7 5.733 7.325C6.058 7.65 6.583 7.65 6.908 7.325Z"
                  fill="currentColor"
                />
              </svg>
            </div>

            <!-- Label text area -->
            <div class="text-area">
              <div class="label-row">
                <span part="drop-zone-label" class="label-slot-wrapper">
                  <slot name="label">
                    <span class="label-action">Click to upload</span>
                    <span class="label-text">or drag and drop</span>
                  </slot>
                </span>
              </div>
              <span part="drop-zone-hint" class="hint-slot-wrapper" ?hidden=${!this._hasHintSlotContent}>
                <slot name="hint" @slotchange=${this._handleHintSlotChange}></slot>
              </span>
            </div>
          </div>

          <!-- Drag-over file preview -->
          ${this._isDragOver && this._dragPreviewType
            ? html`
                <div part="drop-zone-preview" class="drag-preview" aria-hidden="true">
                  <div class="drag-preview__page">
                    <!--
                      Page shape matches asc-file-upload-item: white body with translucent
                      stroke, light gray fold.
                      Stroke rgba(0,0,0,0.12) — no-token: translucent overlay, no alpha token
                      at this opacity. Fold #F2F4F7 — no-token: decorative one-off.
                    -->
                    <svg viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M2 0h20l10 10v28a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2z"
                        fill="#fff"
                        stroke="rgba(0,0,0,0.12)"
                        stroke-width="1"
                      />
                      <path d="M22 0 32 10H24a2 2 0 0 1-2-2V0z" fill="#F2F4F7" />
                    </svg>
                  </div>
                  <div
                    class="drag-preview__badge"
                    style="background: ${resolveFileIconColor(this._dragPreviewType)};"
                  >
                    ${this._dragPreviewType}
                  </div>
                </div>
              `
            : ''}
        </button>

        <!-- File queue list -->
        <ul
          part="file-list"
          class="file-list"
          role="list"
          ?hidden=${!showList}
          aria-label="Queued files"
        >
          ${this._files.map(
            (entry) => html`
              <li>
                <asc-file-upload-item
                  .file=${entry.file}
                  status=${entry.status}
                  progress=${entry.progress}
                  error-message=${entry.errorMessage ?? ''}
                  ?disabled=${this.disabled}
                  @asc-file-upload-retry=${this._handleItemRetry}
                  @asc-file-upload-remove=${this._handleItemRemove}
                ></asc-file-upload-item>
              </li>
            `
          )}
        </ul>

        <!-- Single-file mode: show outside list when not multiple -->
        ${!this.multiple && this._files.length > 0
          ? html`
              <asc-file-upload-item
                .file=${this._files[0].file}
                status=${this._files[0].status}
                progress=${this._files[0].progress}
                error-message=${this._files[0].errorMessage ?? ''}
                ?disabled=${this.disabled}
                @asc-file-upload-retry=${this._handleItemRetry}
                @asc-file-upload-remove=${this._handleItemRemove}
              ></asc-file-upload-item>
            `
          : ''}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-file-upload': AscFileUpload;
  }
}
